<template>
  <BlurringPreparation
    v-if="!blurredImagesReady || (usingBlurredImages && !technicalCategoryId)"
    v-model="blurredModalVisible"
    :toggleActive="toggleActive"
    @update:modelValue="blurredModalVisible = $event"
  />
  <div v-else-if="thumbnailsAvailable !== false" class="gallery-container">
    <div class="gallery-header">
      <div>
        <h3 class="gallery-title">{{ $t("imageNavigation.photoGallery") }}</h3>
        <p class="gallery-subtitle">
          ({{ imagesCount }}) {{ $t("imageNavigation.images") }}
        </p>
      </div>
    </div>

    <BestImagePopUp
      v-if="popupImage"
      :popupImage="popupImage"
      :toggleFullScreenImageActive="toggleFullScreenImageActive"
      :handleImageSelected="handleImageSelected"
      @fullScreen="fullScreen"
      @close="closePopup"
      :mousePositionOnScreen="mousePositionOnScreen"
      :originalImageWidth="originalImageWidth"
      :originalImageHeight="originalImageHeight"
    />

    <div class="gallery-actions-row">
      <DownloadAll :categoryId="technicalCategoryId" />
      <ElTooltip
        v-if="isEmployee && galleryWarningShown"
        :content="$t('csvVisualizer.viewInVisualizer')"
        placement="bottom"
      >
        <button class="csv-visualizer-btn" @click="openCsvVisualizer">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
            />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <line x1="10" y1="9" x2="8" y2="9" />
          </svg>
        </button>
      </ElTooltip>
    </div>
    <div class="cameras-toggle-row">
      <button
        class="cameras-toggle"
        :class="{'cameras-toggle--active': camerasDisplayed}"
        @click="$emit('toggleCameras')"
      >
        <span class="cameras-toggle__thumb" />
      </button>
      <span class="cameras-toggle-label">{{
        $t("imageNavigation.cameras")
      }}</span>
    </div>
    <FullScreenImage
      v-if="fullScreenImageActive"
      :image="selectedImage"
      :is-loading="imageLoading"
      :selectedImageIndex="selectedImageIndex"
      :imagesCount="imagesCount"
      :calculateOffsetForFullScreen="calculateOffsetForFullScreen"
      :originalImageWidth="originalImageWidth"
      :originalImageHeight="originalImageHeight"
      :initialAnnotations="allAnnotations"
      :addAnnotationMarkerToModel="props.addAnnotationMarkerToModel"
      :highlightAnnotationId="pendingAnnotationId"
      :isEmployee="props.isEmployee"
      :currentUserId="props.currentUserId"
      @close="handleFullScreenClosed"
      @nextImage="nextImage"
      @previousImage="previousImage"
    />

    <ImageList
      ref="imageListComponentRef"
      :images="images"
      @image-selected="handleImageSelected"
      @image-loading-state="handleImageLoadingState"
      @scroll-stopped="handleScrollStopped"
      @page-size-calculated="handlePageSizeCalculated"
      @setupBesPopUpImage="setupBesPopUpImage"
      @bestImageChanged="handleBestImageChanged"
      :bestImage="bestImage"
      :fullScreenImageActive="fullScreenImageActive"
      :originalImageWidth="originalImageWidth"
      :originalImageHeight="originalImageHeight"
    />
  </div>
  <ThumbnailsPreparation
    v-else
    v-model="thumbnailsModalVisible"
    :toggleActive="toggleActive"
    @update:modelValue="thumbnailsModalVisible = $event"
  />
</template>

<script setup>
import {ref, nextTick, onMounted, watch} from "vue";
import {useRouter} from "vue-router";
import ImageList from "./ImageList.vue";
import DownloadAll from "@/components/Gallery/DownloadAll.vue";
import API from "@/api/API";
import annotationsApi from "@/api/endpoints/annotations.js";
import {isStaging} from "@/utils/env.js";
import BestImagePopUp from "@/components/Gallery/BestImagePopUp.vue";
import FullScreenImage from "@/components/Gallery/FullScreenImage.vue";
import ThumbnailsPreparation from "@/components/Gallery/ThumbnailsPreparation.vue";
import BlurringPreparation from "@/components/Gallery/BlurringPreparation.vue";
import {ElMessage, ElTooltip} from "element-plus";
import {useI18n} from "vue-i18n";

const props = defineProps([
  "projectId",
  "technicalCategoryId",
  "initializeGalleryMode",
  "bestImage",
  "toggleFullScreenImageActive",
  "fullScreenImageActive",
  "bestImagesPoints",
  "mousePositionOnScreen",
  "calculateOffsetForFullScreen",
  "originalImageWidth",
  "originalImageHeight",
  "changeCursorToLoading",
  "thumbnailsAvailable",
  "setThumbnailsAvailable",
  "toggleActive",
  "isEmployee",
  "galleryWarningShown",
  "usingBlurredImages",
  "fetchCamerasWithImages",
  "camerasDisplayed",
  "addAnnotationMarkerToModel",
  "annotationOpenRequest",
  "currentUserId",
]);

const emit = defineEmits(["fullScreen", "openCsvVisualizer", "toggleCameras"]);

const router = useRouter();
const {t} = useI18n();

const openCsvVisualizer = () => {
  emit("openCsvVisualizer", {
    imagesCountRc: imagesCountRc.value,
    imagesCountNonRc: imagesCountNonRc.value,
    useRcFlag: useRcFlag.value,
    technicalCategoryId: props.technicalCategoryId,
  });
};

const images = ref(Array(50).fill(null));
const loading = ref(false);
const imagesCount = ref(0);
const imagesCountRc = ref(0);
const imagesCountNonRc = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const fetchedPages = ref(new Set());
const popupImage = ref(null);
const selectedImage = ref(null);
const selectedImageIndex = ref(null);
const imageLoading = ref(false);
const imageListComponentRef = ref(null);
const originalImageSize = ref(null);
const previousIndex = ref(0);
const useRcFlag = ref(true);
const thumbnailsModalVisible = ref(true);
const blurredModalVisible = ref(false);
const blurredImagesReady = ref(true);
const usingCamerasEndpoint = ref(false);

const allAnnotations = ref([]);
const pendingAnnotationId = ref(null);

watch(
  () => props.annotationOpenRequest,
  async (req) => {
    if (!req) return;
    if (!staging) return; // TODO remove me when annotations should go to prod
    pendingAnnotationId.value = req.annotationId ?? null;

    // Refresh so newly-created annotations (not in the initial fetch) are available
    if (props.projectId && pendingAnnotationId.value != null) {
      try {
        const response = await annotationsApi.getAnnotations(props.projectId);
        allAnnotations.value = response.data?.results ?? response.data ?? [];
      } catch (e) {
        // Fall back to the cached list
      }
    }

    const image = images.value[req.cameraIndex];
    if (image) {
      handleImageSelected(image, true);
    }
  },
);

onMounted(async () => {
  // TODO remove isStaging when annotations should go to prod
  if (isStaging && props.projectId) {
    try {
      const response = await annotationsApi.getAnnotations(props.projectId);
      allAnnotations.value = response.data?.results ?? response.data ?? [];
    } catch (e) {
      console.error("Failed to fetch annotations:", e);
    }
  }
});

const previousImage = () => {
  imageListComponentRef.value.previousImage();
};

const nextImage = () => {
  imageListComponentRef.value.nextImage();
};

const projectAnnotationsOntoModel = () => {
  if (!props.addAnnotationMarkerToModel) return;
  allAnnotations.value.forEach((ann) => {
    if (ann.image_index != null && ann.norm_x != null && ann.norm_y != null) {
      props.addAnnotationMarkerToModel(
        ann.image_index,
        ann.norm_x,
        ann.norm_y,
        ann,
      );
    }
  });
};

const handlePageSizeCalculated = async (calculatedPageSize) => {
  props.changeCursorToLoading();
  pageSize.value = calculatedPageSize;

  // Try new flow: getCameras endpoint returns cameras with associated images
  if (props.fetchCamerasWithImages) {
    const cameraPageData = await props.fetchCamerasWithImages(
      1,
      pageSize.value,
    );
    if (
      cameraPageData &&
      cameraPageData.results &&
      cameraPageData.results.filter(Boolean).length > 0
    ) {
      await populateFromCamerasEndpoint(cameraPageData);
      return;
    }
  }

  // Fallback: old flow
  await fetchImages();
  props.initializeGalleryMode(originalImageSize.value, imagesCount.value);
  projectAnnotationsOntoModel();
};

const populateFromCamerasEndpoint = async ({results, count}) => {
  if (props.usingBlurredImages && !props.technicalCategoryId) {
    blurredModalVisible.value = true;
    blurredImagesReady.value = false;
    return;
  }

  const validImages = results.filter(Boolean);

  const thumbsAvailable = props.setThumbnailsAvailable(validImages);
  if (!thumbsAvailable) {
    return setTimeout(async () => {
      const retryData = await props.fetchCamerasWithImages(1, pageSize.value);
      if (retryData) await populateFromCamerasEndpoint(retryData);
    }, 30000);
  }

  if (props.originalImageWidth && props.originalImageHeight) {
    originalImageSize.value = {
      width: props.originalImageWidth,
      height: props.originalImageHeight,
    };
  } else {
    originalImageSize.value = await calculateImageDimensions(validImages[0]);
  }

  imagesCount.value = count;
  imagesCountRc.value = count;
  fetchedPages.value.add(1);

  images.value = Array(count)
    .fill(null)
    .map((_, index) => results[index] || null);

  usingCamerasEndpoint.value = true;

  // Prefetch 3 pages ahead
  for (let i = 2; i <= 4; i++) {
    prefetchNextCameraPage(i);
  }

  await nextTick();
  props.initializeGalleryMode(originalImageSize.value, imagesCount.value);
  projectAnnotationsOntoModel();
};

// Fetch full image data by pk
const fetchFullImageData = async (pk) => {
  try {
    const response = await API.projectFileCategories.getProjectImage(pk);
    return response.data;
  } catch (error) {
    console.error("Error fetching full image data:", error);
    return null;
  }
};

// Helper function to check if image has full data needed for fullscreen
const hasFullImageData = (image) => {
  return image && image.downloadUrl && image.compressedUrl && image.metadata;
};

const fetchNonRcCount = async () => {
  try {
    const response = await API.projectFileCategories.listProjectFiles(
      props.technicalCategoryId,
      1,
      1,
      false,
    );
    if (response.data && response.data.count) {
      imagesCountNonRc.value = response.data.count;
    }
  } catch (error) {
    console.error("Error fetching non-RC count:", error);
  }
};

const fetchImages = async () => {
  try {
    if (props.usingBlurredImages && !props.technicalCategoryId) {
      blurredModalVisible.value = true;
      blurredImagesReady.value = false;
      return;
    }

    if (!props.technicalCategoryId) return;

    loading.value = true;
    const response = await API.projectFileCategories.listProjectFiles(
      props.technicalCategoryId,
      currentPage.value,
      pageSize.value,
      useRcFlag.value,
    );
    if (
      useRcFlag.value &&
      (!response.data ||
        !response.data.results ||
        response.data.results.length === 0)
    ) {
      useRcFlag.value = false;
      return fetchImages();
    }

    if (props.usingBlurredImages && response.data.results.length === 0) {
      blurredModalVisible.value = true;
      blurredImagesReady.value = false;
      return;
    }

    const oldThumbnailsAvailable = props.thumbnailsAvailable;
    let thumbsAvailable = props.setThumbnailsAvailable(response.data.results);

    if (!thumbsAvailable) {
      return setTimeout(() => {
        fetchImages();
      }, 30000);
    } else if (oldThumbnailsAvailable === false) {
      thumbnailsModalVisible.value = false;
      props.changeCursorToLoading();
      ElMessage.success({
        message: t("imageNavigation.thumbnailPreparation.galleryReady"),
        showClose: true,
        duration: 0,
      });
    }

    if (props.originalImageWidth && props.originalImageHeight) {
      originalImageSize.value = {
        width: props.originalImageWidth,
        height: props.originalImageHeight,
      };
    } else {
      originalImageSize.value = await calculateImageDimensions(
        response.data.results[0],
      );
    }

    imagesCount.value = response.data.count;
    if (useRcFlag.value) {
      imagesCountRc.value = response.data.count;
      fetchNonRcCount();
    } else {
      imagesCountNonRc.value = response.data.count;
    }
    fetchedPages.value.add(currentPage.value);

    images.value = Array(imagesCount.value)
      .fill(null)
      .map((_, index) => response.data.results[index] || null);

    // Prefetch 3 pages ahead
    for (let i = 1; i <= 3; i++) {
      prefetchNextPage(currentPage.value + i);
    }
  } catch (error) {
    console.error("Error fetching images:", error);
  } finally {
    loading.value = false;
  }
};

const prefetchNextCameraPage = async (pageIndex) => {
  if (
    fetchedPages.value.has(pageIndex) ||
    (pageIndex - 1) * pageSize.value >= imagesCount.value
  ) {
    return;
  }

  try {
    const data = await props.fetchCamerasWithImages(pageIndex, pageSize.value);
    if (!data) return;
    const startIndex = (pageIndex - 1) * pageSize.value;
    data.results.forEach((item, idx) => {
      images.value[startIndex + idx] = item;
    });

    fetchedPages.value.add(pageIndex);
  } catch (error) {
    console.error(`Error prefetching camera page ${pageIndex}:`, error);
  } finally {
    handleBestImageChanged();
  }
};

const prefetchPreviousCameraPage = async (pageIndex) => {
  if (pageIndex < 1 || fetchedPages.value.has(pageIndex)) {
    return;
  }

  try {
    const data = await props.fetchCamerasWithImages(pageIndex, pageSize.value);
    if (!data) return;
    const startIndex = (pageIndex - 1) * pageSize.value;
    data.results.forEach((item, idx) => {
      images.value[startIndex + idx] = item;
    });

    fetchedPages.value.add(pageIndex);
  } catch (error) {
    console.error(
      `Error prefetching previous camera page ${pageIndex}:`,
      error,
    );
  } finally {
    handleBestImageChanged();
  }
};

const handleScrollStopped = async (index) => {
  if (!props.technicalCategoryId && !usingCamerasEndpoint.value) return;

  // Determine scroll direction
  const isScrollingDown = index > previousIndex.value;
  const isScrollingUp = index < previousIndex.value;

  // Calculate current and previous page indices
  const currentPageFromIndex = Math.floor(index / pageSize.value) + 1;
  const previousPageFromIndex =
    Math.floor(previousIndex.value / pageSize.value) + 1;

  const isScrollingWithinSamePage =
    currentPageFromIndex === previousPageFromIndex;

  // Prefetch based on scroll direction and position - only if scrolling within the same page
  if (isScrollingDown && index >= 2 && isScrollingWithinSamePage) {
    // Scrolling down within the same page - prefetch next pages
    if (usingCamerasEndpoint.value) {
      prefetchNextCameraPage(currentPage.value + 4);
    } else {
      prefetchNextPage(currentPage.value + 4);
    }
  } else if (isScrollingUp && index >= 2 && isScrollingWithinSamePage) {
    // Scrolling up within the same page - prefetch previous pages
    if (usingCamerasEndpoint.value) {
      prefetchPreviousCameraPage(currentPage.value - 4);
    } else {
      prefetchPreviousPage(currentPage.value - 4);
    }
  }

  // Update previous index for next comparison
  previousIndex.value = index;

  const pageIndex = Math.floor(index / pageSize.value) + 1;

  if (pageIndex === currentPage.value || loading.value) return;

  try {
    loading.value = true;

    if (!fetchedPages.value.has(pageIndex)) {
      if (usingCamerasEndpoint.value) {
        const data = await props.fetchCamerasWithImages(
          pageIndex,
          pageSize.value,
        );
        if (data) {
          const startIndex = (pageIndex - 1) * pageSize.value;
          data.results.forEach((item, idx) => {
            images.value[startIndex + idx] = item;
          });
          fetchedPages.value.add(pageIndex);
        }
      } else {
        const response = await API.projectFileCategories.listProjectFiles(
          props.technicalCategoryId,
          pageIndex,
          pageSize.value,
          useRcFlag.value,
        );

        const startIndex = (pageIndex - 1) * pageSize.value;
        response.data.results.forEach((item, idx) => {
          images.value[startIndex + idx] = item;
        });

        fetchedPages.value.add(pageIndex);
      }
    }

    currentPage.value = pageIndex;

    // Prefetch 3 pages ahead
    for (let i = 1; i <= 3; i++) {
      if (usingCamerasEndpoint.value) {
        prefetchNextCameraPage(pageIndex + i);
      } else {
        prefetchNextPage(pageIndex + i);
      }
    }

    // Prefetch 3 pages behind
    for (let i = 1; i <= 3; i++) {
      if (usingCamerasEndpoint.value) {
        prefetchPreviousCameraPage(pageIndex - i);
      } else {
        prefetchPreviousPage(pageIndex - i);
      }
    }

    cleanupPages();
  } catch (error) {
    console.error("Error fetching additional images:", error);
  } finally {
    loading.value = false;

    handleBestImageChanged();
  }
};

const prefetchNextPage = async (pageIndex) => {
  if (
    fetchedPages.value.has(pageIndex) ||
    (pageIndex - 1) * pageSize.value >= imagesCount.value ||
    !props.technicalCategoryId
  ) {
    return;
  }

  try {
    const response = await API.projectFileCategories.listProjectFiles(
      props.technicalCategoryId,
      pageIndex,
      pageSize.value,
      useRcFlag.value,
    );
    const startIndex = (pageIndex - 1) * pageSize.value;
    response.data.results.forEach((item, idx) => {
      images.value[startIndex + idx] = item;
    });

    fetchedPages.value.add(pageIndex);
  } catch (error) {
    console.error(`Error prefetching page ${pageIndex}:`, error);
  } finally {
    handleBestImageChanged();
  }
};

const prefetchPreviousPage = async (pageIndex) => {
  if (
    pageIndex < 1 ||
    fetchedPages.value.has(pageIndex) ||
    !props.technicalCategoryId
  ) {
    return;
  }

  try {
    const response = await API.projectFileCategories.listProjectFiles(
      props.technicalCategoryId,
      pageIndex,
      pageSize.value,
      useRcFlag.value,
    );
    const startIndex = (pageIndex - 1) * pageSize.value;
    response.data.results.forEach((item, idx) => {
      images.value[startIndex + idx] = item;
    });

    fetchedPages.value.add(pageIndex);
  } catch (error) {
    console.error(`Error prefetching previous page ${pageIndex}:`, error);
  } finally {
    handleBestImageChanged();
  }
};

const cleanupPages = () => {
  const currentPageIndex = currentPage.value;
  const pagesToKeep = new Set();

  // Keep current page, 3 pages ahead, and 3 pages behind
  for (let i = -3; i <= 3; i++) {
    const pageToKeep = currentPageIndex + i;
    if (pageToKeep >= 1) {
      pagesToKeep.add(pageToKeep);
    }
  }

  const pagesToRemove = Array.from(fetchedPages.value).filter(
    (pageNum) => !pagesToKeep.has(pageNum),
  );

  pagesToRemove.forEach((pageNum) => {
    const startIndex = (pageNum - 1) * pageSize.value;
    const endIndex = Math.min(startIndex + pageSize.value, images.value.length);

    for (let i = startIndex; i < endIndex; i++) {
      images.value[i] = null;
    }

    fetchedPages.value.delete(pageNum);
  });
};

function setPopUpImage(index, retryCount = 0) {
  // aq
  if (images.value[index]) {
    popupImage.value = images.value[index];
    selectedImage.value = images.value[index];
    selectedImageIndex.value = index;
    imageLoading.value = false;
  } else if (retryCount < 30) {
    setTimeout(() => {
      setPopUpImage(index, loading.value ? retryCount : retryCount + 1);
    }, 500);
  } else {
    console.warn(
      `Failed to set popup image for index ${index} after 30 retries`,
    );
  }
}

function setupBesPopUpImage(index) {
  if (index === null || index === undefined) {
    if (index === undefined) popupImage.value = null;
    else popupImage.value = -1;
    cleanImageCoordinates();
    return;
  }
  setPopUpImage(index);
}

const handleImageSelected = async (image, openFullScreen = true) => {
  if (!image) {
    // If image is null/undefined, set loading state to true
    imageLoading.value = true;
    return;
  }

  // Set loading state while potentially fetching full data
  imageLoading.value = true;

  // Check if image has full data needed for fullscreen
  if (!hasFullImageData(image) && image.pk) {
    // Fetch full image data
    const fullImageData = await fetchFullImageData(image.pk);
    if (fullImageData) {
      selectedImage.value = {...image, ...fullImageData};
    } else {
      selectedImage.value = image;
    }
  } else {
    selectedImage.value = image;
  }

  selectedImageIndex.value = images.value.indexOf(image);
  imageLoading.value = false;
  if (openFullScreen) props.toggleFullScreenImageActive(true);
};

const handleImageLoadingState = (loading) => {
  imageLoading.value = loading;
};

const handleFullScreenClosed = () => {
  props.toggleFullScreenImageActive(false);
  popupImage.value = null;
  pendingAnnotationId.value = null;
};

const handleBestImageChanged = () => {
  if (loading.value) return setTimeout(handleBestImageChanged, 500);
  if (props.bestImagesPoints) {
    props.bestImagesPoints.forEach((point) => {
      const image = images.value[point.originalIndex];
      if (image) {
        image.coordinatesOnImage = point.coordinatesOnImage;
      }
    });
  }
};

const cleanImageCoordinates = () => {
  images.value.forEach((image) => {
    if (image) {
      image.coordinatesOnImage = null;
    }
  });
};

const calculateImageDimensions = async (image) => {
  if (!image || !image.downloadUrl) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const dimensions = {
        width: img.width,
        height: img.height,
      };

      img.onload = null;
      img.onerror = null;
      img.src = "";

      resolve(dimensions);
    };

    img.onerror = () => {
      const error = new Error("Failed to load image for size calculation");
      console.error(error);

      img.onload = null;
      img.onerror = null;
      img.src = "";

      reject(error);
    };

    img.src = image.downloadUrl;
  });
};

const closePopup = () => {
  popupImage.value = null;
};
</script>

<style lang="scss" scoped>
.gallery-container {
  width: 300px;
  height: 100vh;
  background-color: var(--color-dark-bg-primary);
  position: fixed;
  right: 0;
  top: 0;
  box-shadow:
    -12px 0 20px -6px rgba(0, 0, 0, 0.9),
    0 0 10px rgba(0, 0, 0, 0.5);
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.gallery-title {
  color: var(--color-white);
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.gallery-subtitle {
  color: var(--color-white);
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  text-align: start;
}

.gallery-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-right: 16px;
}

.csv-visualizer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-top: 16px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: var(--color-white);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}
.cameras-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
}

.cameras-toggle-label {
  color: var(--color-white);
  font-size: 13px;
  font-weight: 400;
}

.cameras-toggle {
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  background-color: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s;
  padding: 0;
  outline: none;

  &--active {
    background-color: var(--color-gree300);
  }

  &__thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: white;
    transition: transform 0.2s;
  }

  &--active &__thumb {
    transform: translateX(18px);
  }
}
</style>
